import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";

/**
 * Detects new deployments by comparing the ETag of index.html against the
 * one seen when the running bundle was loaded.
 */
@Injectable({
  providedIn: "root",
})
export class EtagService {
  private readonly checkUrl = "/index.html";
  private readonly etagKey = "app-etag";

  constructor(private readonly http: HttpClient) {}

  /**
   * Emits true when the deployed index.html differs from the one this tab
   * started with.
   *
   * The new ETag is deliberately NOT persisted here. Persisting before the
   * user reloads makes a dismissed prompt permanent: the next check would
   * compare against the new value and conclude the tab is up to date while it
   * is still running the old bundle.
   */
  hasNewVersion(): Observable<boolean> {
    return this.headIndex().pipe(
      map((etag) => {
        if (!etag) {
          return false;
        }

        const storedEtag = localStorage.getItem(this.etagKey);

        // First load in this browser: seed the baseline rather than report a
        // phantom update.
        if (!storedEtag) {
          localStorage.setItem(this.etagKey, etag);
          return false;
        }

        if (etag !== storedEtag) {
          console.log("New version detected:", etag, "previous:", storedEtag);
        } else {
          console.log("Version is up to date:", etag);
        }

        return etag !== storedEtag;
      }),
      catchError((error) => {
        if (error?.status !== 304) {
          console.error("Version check failed", error);
        }
        return of(false);
      }),
    );
  }

  /** Records the currently deployed ETag as the baseline for this tab. */
  acceptCurrentVersion(): Observable<void> {
    return this.headIndex().pipe(
      map((etag) => {
        if (etag) {
          localStorage.setItem(this.etagKey, etag);
        }
      }),
      catchError(() => of(undefined)),
    );
  }

  private headIndex(): Observable<string | null> {
    const headers = new HttpHeaders({
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
    });

    return this.http
      .head(`${this.checkUrl}?n=${Date.now()}`, {
        headers,
        observe: "response",
        responseType: "text",
      })
      .pipe(map((response) => response.headers.get("ETag")));
  }
}
